import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const ReportCustomization = ({
  offers,
  selectedOfferIds,
  onOfferToggle,
  includePropertyDetails,
  setIncludePropertyDetails,
  includeAgentNotes,
  setIncludeAgentNotes,
  aiEnabled,
  setAiEnabled,
  hasProAccess = false,
  agentNameDisplay,
  setAgentNameDisplay,
  buyerNameDisplay,
  setBuyerNameDisplay,
  subscriptionTier,
  onUpgrade,
  agentNameDisplayChange,
  buyerNameDisplayChange
}) => {
  const isAiAvailable = subscriptionTier === 'pro' || subscriptionTier === 'enterprise';

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="CheckSquare" size={20} />
          Select Offers to Include
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Choose which offers to include in the comparison report (minimum 1 required)
        </p>
        <div className="space-y-3">
          {offers?.map((offer, index) => (
            <div
              key={offer?.id}
              className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                checked={selectedOfferIds?.includes(offer?.id)}
                onChange={(e) => onOfferToggle(offer?.id, e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-foreground">Offer {index + 1}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                    {offer?.buyerName}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 data-text">
                    <Icon name="DollarSign" size={14} />
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })?.format(offer?.offerPrice)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    {new Date(offer.closingDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="CreditCard" size={14} />
                    {offer?.financingType}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Settings" size={20} />
          Report Content Options
        </h3>
        <div className="space-y-4">
          <Checkbox
            label="Include Property Details"
            description="Show property address, owner information, and listing price"
            checked={includePropertyDetails}
            onChange={(e) => setIncludePropertyDetails(e?.target?.checked)}
          />
          <Checkbox
            label="Include Agent Notes"
            description="Display internal notes for each offer in the report"
            checked={includeAgentNotes}
            onChange={(e) => setIncludeAgentNotes(e?.target?.checked)}
          />
          
          <div className="pt-2 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Eye" size={16} />
              Privacy Options
            </h4>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Agent Name Display
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="agentNameDisplay"
                      value="full"
                      checked={agentNameDisplay === 'full'}
                      onChange={(e) => setAgentNameDisplay(e?.target?.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Show full name</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="agentNameDisplay"
                      value="initials"
                      checked={agentNameDisplay === 'initials'}
                      onChange={(e) => setAgentNameDisplay(e?.target?.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Show initials only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="agentNameDisplay"
                      value="hidden"
                      checked={agentNameDisplay === 'hidden'}
                      onChange={(e) => setAgentNameDisplay(e?.target?.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Hide agent name</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Buyer Name Display
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="buyerNameDisplay"
                      value="full"
                      checked={buyerNameDisplay === 'full'}
                      onChange={(e) => setBuyerNameDisplay(e?.target?.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Show full name</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="buyerNameDisplay"
                      value="initials"
                      checked={buyerNameDisplay === 'initials'}
                      onChange={(e) => setBuyerNameDisplay(e?.target?.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Show initials only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="buyerNameDisplay"
                      value="hidden"
                      checked={buyerNameDisplay === 'hidden'}
                      onChange={(e) => setBuyerNameDisplay(e?.target?.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Hide buyer name</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Sparkles" size={20} className="text-accent" />
            <h3 className="text-base md:text-lg font-semibold text-foreground">AI-Powered Insights</h3>
          </div>
          {isAiAvailable && (
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent-foreground font-medium">
                PRO
              </span>
            </div>
          )}
        </div>

        {isAiAvailable ? (
          <div className="space-y-4">
            {/* AI Analysis Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start gap-3 flex-1">
                <Icon name="Sparkles" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <label className="text-sm font-medium text-foreground cursor-pointer">
                    AI-Powered Analysis
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Include intelligent offer summaries and key differences
                  </p>
                  {!hasProAccess && (
                    <p className="text-xs text-warning font-medium mt-1">
                      Pro subscription required
                    </p>
                  )}
                </div>
              </div>
              <Checkbox
                checked={aiEnabled && hasProAccess}
                onChange={(e) => setAiEnabled(e?.target?.checked)}
                disabled={!hasProAccess}
              />
            </div>
            {aiEnabled && (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} className="mt-0.5 text-accent-foreground flex-shrink-0" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">AI Features Include:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Automated offer summarization</li>
                      <li>• Key difference highlighting</li>
                      <li>• Neutral, factual explanations</li>
                      <li>• No recommendations or bias</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-muted rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Icon name="Lock" size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-foreground font-medium mb-1">
                  Upgrade to unlock AI features
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Get AI-powered offer summaries, key difference highlights, and neutral seller-friendly explanations with a Pro subscription.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onUpgrade}
                  iconName="ArrowUpRight"
                  iconPosition="right"
                >
                  Upgrade to Pro
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportCustomization;